// @ts-nocheck
import React, { useState } from 'react';

export type SearchOperator = 'AND' | 'OR';

interface SearchRule {
  field: string;
  operator: 'contains' | 'equals' | 'starts_with' | 'ends_with' | 'not_contains';
  value: string;
}

interface AdminAdvancedSearchProps {
  fields: Array<{ id: string; label: string }>;
  onSearch: (rules: SearchRule[], operator: SearchOperator) => void;
  onClear: () => void;
  className?: string;
}

export const AdminAdvancedSearch: React.FC<AdminAdvancedSearchProps> = ({
  fields,
  onSearch,
  onClear,
  className = '',
}) => {
  const [operator, setOperator] = useState<SearchOperator>('AND');
  const [rules, setRules] = useState<SearchRule[]>([
    { field: fields[0]?.id || '', operator: 'contains', value: '' },
  ]);
  const [isExpanded, setIsExpanded] = useState(false);

  const addRule = () => {
    setRules([...rules, { field: fields[0]?.id || '', operator: 'contains', value: '' }]);
  };

  const removeRule = (index: number) => {
    if (rules.length > 1) {
      setRules(rules.filter((_, i) => i !== index));
    }
  };

  const updateRule = (index: number, updates: Partial<SearchRule>) => {
    setRules(rules.map((rule, i) => (i === index ? { ...rule, ...updates } : rule)));
  };

  const handleSearch = () => {
    const validRules = rules.filter((rule) => rule.value.trim() !== '');
    if (validRules.length > 0) {
      onSearch(validRules, operator);
    }
  };

  const handleClear = () => {
    setRules([{ field: fields[0]?.id || '', operator: 'contains', value: '' }]);
    setOperator('AND');
    onClear();
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className={`w-full sm:w-auto px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation ${className}`}
        aria-label="Open advanced search"
      >
        <span className="material-symbols-outlined text-sm">filter_alt</span>
        <span>Advanced Search</span>
      </button>
    );
  }

  return (
    <div className={`bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-luna-text-light dark:text-luna-text-dark">
          Advanced Search
        </h3>
        <button
          onClick={() => setIsExpanded(false)}
          className="text-luna-text-light/50 dark:text-luna-text-dark/50 hover:text-luna-text-light dark:hover:text-luna-text-dark p-2 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
          aria-label="Close advanced search"
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      {/* Operator Selection */}
      <div className="flex items-center gap-2">
        <label htmlFor="match-operator" className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark">
          Match:
        </label>
        <div id="match-operator" className="flex gap-2">
          <button
            onClick={() => setOperator('AND')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] touch-manipulation ${
              operator === 'AND'
                ? 'bg-luna-secondary text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-luna-text-light dark:text-luna-text-dark'
            }`}
            aria-label="Use AND operator"
          >
            All (AND)
          </button>
          <button
            onClick={() => setOperator('OR')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] touch-manipulation ${
              operator === 'OR'
                ? 'bg-luna-secondary text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-luna-text-light dark:text-luna-text-dark'
            }`}
            aria-label="Use OR operator"
          >
            Any (OR)
          </button>
        </div>
      </div>

      {/* Search Rules */}
      <div className="space-y-3">
        {rules.map((rule, index) => (
          <div key={index} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
            <select
              value={rule.field}
              onChange={(e) => updateRule(index, { field: e.target.value })}
              className="w-full sm:w-auto px-3 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark text-sm min-h-[44px] touch-manipulation"
              aria-label={`Field for rule ${index + 1}`}
            >
              {fields.map((field) => (
                <option key={field.id} value={field.id}>
                  {field.label}
                </option>
              ))}
            </select>

            <select
              value={rule.operator}
              onChange={(e) => updateRule(index, { operator: e.target.value as SearchRule['operator'] })}
              className="w-full sm:w-auto px-3 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark text-sm min-h-[44px] touch-manipulation"
              aria-label={`Operator for rule ${index + 1}`}
            >
              <option value="contains">Contains</option>
              <option value="equals">Equals</option>
              <option value="starts_with">Starts with</option>
              <option value="ends_with">Ends with</option>
              <option value="not_contains">Does not contain</option>
            </select>

            <input
              type="text"
              value={rule.value}
              onChange={(e) => updateRule(index, { value: e.target.value })}
              placeholder="Search value..."
              className="flex-1 px-4 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] touch-manipulation"
              aria-label={`Search value for rule ${index + 1}`}
            />

            {rules.length > 1 && (
              <button
                onClick={() => removeRule(index)}
                className="px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                aria-label={`Remove rule ${index + 1}`}
              >
                <span className="material-symbols-outlined text-sm">delete</span>
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <button
          onClick={addRule}
          className="w-full sm:w-auto px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-luna-text-light dark:text-luna-text-dark rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
        >
          <span className="material-symbols-outlined text-sm">add</span>
          <span>Add Rule</span>
        </button>
        <button
          onClick={handleSearch}
          className="flex-1 px-4 py-2.5 bg-luna-secondary text-white rounded-lg hover:bg-luna-secondary/90 min-h-[44px] touch-manipulation"
        >
          Search
        </button>
        <button
          onClick={handleClear}
          className="w-full sm:w-auto px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-luna-text-light dark:text-luna-text-dark rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 min-h-[44px] touch-manipulation"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export type { SearchRule };

